/**
 * Module Health Monitor - Periodic per-module health assessment with dependency cascade.
 *
 * ARCHITECTURE:
 *   nuvraRuntime.getRegisteredModules() → builds dependency graph each cycle
 *   nuvraRuntime.checkModuleHealth(id)  → direct health per module (or null = "unknown")
 *   cascade algorithm                   → propagates upstream failures to dependents
 *   nuvraRuntime.setModuleHealth(id, h) → stores result back on runtime
 *   eventBus                            → emits module:health_changed, runtime:health_assessed
 *
 * GOVERNANCE RULES (Canonical Ownership Doctrine §XII + Subsystem 6):
 *   1. Modules without healthCheck() report "unknown" - not "healthy".
 *      Unknown is truthful. Fabricated certainty is forbidden.
 *   2. A mix of "unknown" and "healthy" among a set of modules is "degraded".
 *      Partial knowledge is not certainty.
 *   3. If module A is "unhealthy", all direct dependents of A become "degraded"
 *      (not "unhealthy") with cascadedFrom = A's id.
 *   4. Health checks MUST be synchronous and complete in ≤50ms.
 *      The monitor enforces this via the RuntimeModule.healthCheck() contract.
 *   5. This module is Tier 5 (observability only) per the Canonical Ownership Doctrine.
 *      It may NOT mutate NuvraSchema or any Tier 1-4 system content.
 *
 * CYCLE:
 *   assessAll() is called on an interval (default 30s).
 *   assessAll() is also called immediately on start() to provide initial state.
 */

import { nuvraRuntime }                    from "./coreRuntime";
import type { ModuleHealthStatus, ModuleHealthStatusLevel } from "./coreRuntime";
import { eventBus }                        from "./eventBus";
import { logger }                          from "@/system/logger";

/* =========================================================
   INTERNAL SEVERITY ORDERING
   Higher number = worse health.
   Used by worstOf() and aggregateHealth().
   ========================================================= */

const SEVERITY: Record<ModuleHealthStatusLevel, number> = {
  unhealthy: 3,
  degraded:  2,
  unknown:   1,
  healthy:   0,
};

function worstOf(
  a: ModuleHealthStatusLevel,
  b: ModuleHealthStatusLevel,
): ModuleHealthStatusLevel {
  return SEVERITY[a] >= SEVERITY[b] ? a : b;
}

/**
 * Aggregate a set of status levels into a single representative level.
 *
 * Rules:
 *   - Any "unhealthy" → "unhealthy"
 *   - Any "degraded"  → "degraded"
 *   - Mix of "unknown" + "healthy" → "degraded" (partial knowledge ≠ certainty)
 *   - All "unknown"   → "unknown"
 *   - All "healthy"   → "healthy"
 *   - Empty set       → "unknown"
 */
function aggregateHealth(
  statuses: ModuleHealthStatusLevel[],
): ModuleHealthStatusLevel {
  if (statuses.length === 0)             return "unknown";
  if (statuses.includes("unhealthy"))    return "unhealthy";
  if (statuses.includes("degraded"))     return "degraded";
  if (statuses.every(s => s === "unknown")) return "unknown";
  // Mix of "healthy" and "unknown" - partial knowledge is not certainty
  if (statuses.includes("unknown"))      return "degraded";
  return "healthy";
}

/* =========================================================
   MODULE HEALTH MONITOR CLASS
   ========================================================= */

type HealthChangeCallback = (moduleId: string, health: ModuleHealthStatus) => void;

class ModuleHealthMonitor {
  /** In-memory health map - keyed by module ID. */
  private health   = new Map<string, ModuleHealthStatus>();
  private interval: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<HealthChangeCallback>();

  /* ── PUBLIC API ──────────────────────────────────────── */

  /**
   * Start periodic health assessment.
   * Performs an immediate assessment on start, then repeats on intervalMs.
   */
  start(intervalMs = 30_000): void {
    this.stop();
    // Immediate first assessment - consumers see health immediately after boot
    this.assessAll();
    this.interval = setInterval(() => this.assessAll(), intervalMs);
    logger.info("ModuleHealthMonitor started", { intervalMs });
  }

  /** Stop periodic assessment. */
  stop(): void {
    if (this.interval !== null) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  /**
   * Run a full assessment cycle synchronously.
   * Builds the dependency graph, computes direct health, applies cascade,
   * updates the runtime, and emits events for any status transitions.
   */
  assessAll(): void {
    const modules = nuvraRuntime.getRegisteredModules();
    if (modules.length === 0) {
      logger.debug("ModuleHealthMonitor.assessAll: no modules registered yet");
      return;
    }

    // Step 1 - Collect FULL direct health from each module (not just status level).
    //          Preserving the full record here ensures that detail/cascadedFrom from
    //          healthCheck() implementations are propagated to the final health record
    //          when no cascade is applied.
    const directHealth = new Map<string, ModuleHealthStatus>();
    for (const mod of modules) {
      const result = nuvraRuntime.checkModuleHealth(mod.id);
      // null = healthCheck() not implemented → "unknown" with no detail (truthful)
      directHealth.set(mod.id, result ?? { status: "unknown", checkedAt: 0 });
    }

    // Step 2 - Build a lookup map for fast dependency resolution
    const moduleById = new Map(modules.map(m => [m.id, m]));

    // Step 3 - Topological cascade: process dependencies before dependents
    const finalHealth = new Map<string, ModuleHealthStatus>();
    const visited     = new Set<string>();
    const now         = Date.now();

    const compute = (id: string): void => {
      if (visited.has(id)) return;
      visited.add(id);

      const mod = moduleById.get(id);
      if (!mod) return;

      // Ensure all declared dependencies are computed first
      for (const depId of mod.dependencies) {
        compute(depId);
      }

      const directRecord = directHealth.get(id) ?? { status: "unknown" as ModuleHealthStatusLevel, checkedAt: 0 };
      const direct       = directRecord.status;

      // Step 3a - Check upstream cascade from dependencies
      let cascadedStatus: ModuleHealthStatusLevel = direct;
      let cascadedFrom:   string | undefined;

      for (const depId of mod.dependencies) {
        const depHealth = finalHealth.get(depId);
        if (!depHealth) continue;

        if (depHealth.status === "unhealthy") {
          // Unhealthy upstream → this module is "degraded" (not "unhealthy")
          // The module itself hasn't failed - its upstream has
          if (SEVERITY["degraded"] > SEVERITY[cascadedStatus]) {
            cascadedStatus = "degraded";
            // Trace cascade chain to original source
            cascadedFrom = depHealth.cascadedFrom ?? depId;
          }
          break; // First unhealthy upstream wins
        }

        if (depHealth.status === "degraded") {
          if (SEVERITY["degraded"] > SEVERITY[cascadedStatus]) {
            cascadedStatus = "degraded";
            cascadedFrom = depHealth.cascadedFrom ?? depId;
          }
          // Don't break - keep checking for unhealthy (worse)
        }
      }

      // Step 3b - Final health = worst of direct and cascaded
      const finalStatus = worstOf(direct, cascadedStatus);
      const isCascaded  = cascadedFrom !== undefined;

      const healthRecord: ModuleHealthStatus = {
        // Prefer cascade detail (upstream attribution) when cascaded;
        // otherwise pass through the direct health check's detail if present.
        status:    finalStatus,
        checkedAt: now,
        ...(isCascaded && cascadedFrom
          ? { detail: `Degraded by upstream module: ${cascadedFrom}`, cascadedFrom }
          : directRecord.detail
          ? { detail: directRecord.detail }
          : {}),
      };

      finalHealth.set(id, healthRecord);
    };

    for (const mod of modules) compute(mod.id);

    // Step 4 - Commit results: update runtime + emit events for transitions
    for (const [id, newHealth] of finalHealth) {
      const previous = this.health.get(id)?.status ?? null;
      const current  = newHealth.status;

      this.health.set(id, newHealth);
      nuvraRuntime.setModuleHealth(id, newHealth);

      // Always emit module:health_checked (heartbeat)
      eventBus.emit({
        type:      "module:health_checked",
        moduleId:  id,
        status:    current,
        detail:    newHealth.detail,
        checkedAt: now,
      });

      // Emit module:health_changed only on status transition
      if (previous !== current) {
        eventBus.emit({
          type:          "module:health_changed",
          moduleId:      id,
          previous,
          current,
          detail:        newHealth.detail,
          cascadedFrom:  newHealth.cascadedFrom,
        });

        // Notify internal listeners
        for (const fn of this.listeners) {
          try { fn(id, newHealth); } catch { /* isolate listener errors */ }
        }

        const logLevel = current === "unhealthy" ? "error"
                       : current === "degraded"  ? "warn"
                       : "info";
        logger[logLevel](`ModuleHealthMonitor: ${id} → ${current}`, {
          previous,
          current,
          cascadedFrom: newHealth.cascadedFrom,
        });
      }
    }

    // Step 5 - Emit platform summary
    const unhealthyModules = [...finalHealth.entries()]
      .filter(([, h]) => h.status === "unhealthy")
      .map(([id]) => id);
    const degradedModules = [...finalHealth.entries()]
      .filter(([, h]) => h.status === "degraded")
      .map(([id]) => id);
    const overall = aggregateHealth([...finalHealth.values()].map(h => h.status));

    eventBus.emit({
      type:             "runtime:health_assessed",
      overall,
      moduleCount:      finalHealth.size,
      unhealthyModules,
      degradedModules,
    });

    logger.debug("ModuleHealthMonitor.assessAll complete", {
      modules:   finalHealth.size,
      overall,
      unhealthy: unhealthyModules.length,
      degraded:  degradedModules.length,
    });
  }

  /* ── QUERY API ──────────────────────────────────────── */

  /**
   * Get the most recent health status for a module.
   * Returns null if the module has never been assessed.
   */
  getHealth(moduleId: string): ModuleHealthStatus | null {
    return this.health.get(moduleId) ?? null;
  }

  /**
   * Get the aggregate health level for a set of modules.
   * If moduleIds is omitted, aggregates ALL known modules.
   *
   * GOVERNANCE RULE: "unknown" returned only if ALL queried modules are unknown.
   * Mix of "unknown" + "healthy" = "degraded" (partial knowledge ≠ certainty).
   */
  getAggregateHealth(moduleIds?: string[]): ModuleHealthStatusLevel {
    const ids = moduleIds ?? [...this.health.keys()];
    if (ids.length === 0) return "unknown";

    const statuses = ids.map(id => this.health.get(id)?.status ?? "unknown");
    return aggregateHealth(statuses);
  }

  /** All modules currently at "unhealthy" status. */
  getUnhealthyModuleIds(): string[] {
    return [...this.health.entries()]
      .filter(([, h]) => h.status === "unhealthy")
      .map(([id]) => id);
  }

  /** All modules currently at "degraded" status. */
  getDegradedModuleIds(): string[] {
    return [...this.health.entries()]
      .filter(([, h]) => h.status === "degraded")
      .map(([id]) => id);
  }

  /**
   * Read-only snapshot of all known module health states.
   * Suitable for display in admin dashboards or planner pre-flight checks.
   */
  getAllStatuses(): ReadonlyMap<string, ModuleHealthStatus> {
    return this.health;
  }

  /* ── SUBSCRIPTION API ──────────────────────────────── */

  /**
   * Subscribe to health status changes.
   * Callback fires only when status transitions (not on every heartbeat).
   * Returns unsubscribe function.
   */
  onHealthChange(fn: HealthChangeCallback): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /* ── TEST UTILITY ────────────────────────────────────── */

  /**
   * Reset internal state for test isolation.
   *
   * @internal Test use only - do NOT call in production code.
   *
   * Clears: health map, listeners, stops timer.
   * Does NOT affect nuvraRuntime module registrations.
   */
  _resetForTest(): void {
    this.stop();
    this.health.clear();
    this.listeners.clear();
  }
}

/* =========================================================
   SINGLETON
   ========================================================= */

export const moduleHealthMonitor = new ModuleHealthMonitor();
export type  { ModuleHealthStatusLevel };
